import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { FaEdit, FaTrash, FaPlus, FaStar } from 'react-icons/fa';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import ConfirmDialog from '../../Components/utils/ReuseConfirmDialog';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API = process.env.REACT_APP_Backend_Host_Name;

function CustomTabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`faq-tabpanel-${index}`} aria-labelledby={`faq-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}
CustomTabPanel.propTypes = { children: PropTypes.node, index: PropTypes.number.isRequired, value: PropTypes.number.isRequired };
function a11yProps(index) {
    return { id: `faq-tab-${index}`, 'aria-controls': `faq-tabpanel-${index}` };
}

const AdminFAQ = () => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    const [isLoading, setIsLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [categories, setCategories] = useState([]);
    const [faqs, setFaqs] = useState([]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [catRes, faqRes] = await Promise.all([
                axios.get(`${API}/api/v1/faq/admin/categories`, { headers }),
                axios.get(`${API}/api/v1/faq/admin/items`, { headers })
            ]);
            if (catRes.data.success) setCategories(catRes.data.categories);
            if (faqRes.data.success) setFaqs(faqRes.data.faqs);
        } catch {
            showToast('Failed to load FAQ data', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    return (
        <div className="adminFAQDiv">
            {isLoading && <Loader type="checkmate" />}
            <h1 className="primaryHeading">Information Centre</h1>
            <p className="adminFAQSubtext">Manage FAQ categories and questions. Add rich content with links, images, videos, tables, and formatting.</p>

            <div className="adminFAQContent">
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
                            <Tab sx={{ fontSize: '13px' }} label="Categories" {...a11yProps(0)} />
                            <Tab sx={{ fontSize: '13px' }} label="FAQ Items" {...a11yProps(1)} />
                        </Tabs>
                    </Box>
                    <CustomTabPanel value={tabValue} index={0}>
                        <CategoriesTab categories={categories} headers={headers} onUpdate={fetchData} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        <FAQItemsTab categories={categories} faqs={faqs} headers={headers} onUpdate={fetchData} />
                    </CustomTabPanel>
                </Box>
            </div>
        </div>
    );
};

export default AdminFAQ;

// Categories Tab
function CategoriesTab({ categories, headers, onUpdate }) {
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ name: '', color: '#667eea', backgroundColor: '#f8f9ff' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    const resetForm = () => {
        setForm({ name: '', color: '#667eea', backgroundColor: '#f8f9ff' });
        setEditId(null);
        setShowForm(false);
    };

    const handleEdit = (cat) => {
        setEditId(cat._id);
        setForm({ name: cat.name, color: cat.color || '#667eea', backgroundColor: cat.backgroundColor || '#f8f9ff' });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return showToast('Category name is required', 'warning');
        setLoading(true);
        try {
            if (editId) {
                await axios.put(`${API}/api/v1/faq/admin/categories/${editId}`, form, { headers });
                showToast('Category updated', 'success');
            } else {
                await axios.post(`${API}/api/v1/faq/admin/categories`, form, { headers });
                showToast('Category created', 'success');
            }
            resetForm();
            onUpdate();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error saving category', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm.id) return;
        setLoading(true);
        try {
            await axios.delete(`${API}/api/v1/faq/admin/categories/${deleteConfirm.id}`, { headers });
            showToast('Category and its FAQs deleted', 'success');
            setDeleteConfirm({ open: false, id: null });
            onUpdate();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error deleting', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="faqCategoriesTab">
            <div className="faqTabHeader">
                <h3 className="secondaryHeading">FAQ Categories</h3>
                <button className="primaryBtn" onClick={() => { resetForm(); setShowForm(true); }} disabled={loading}>
                    <FaPlus /> Add Category
                </button>
            </div>

            {showForm && (
                <form className="faqCategoryForm" onSubmit={handleSubmit}>
                    <div className="formRow">
                        <div className="inputDiv">
                            <label>Name <span>*</span></label>
                            <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Getting Started" required className="inputField" />
                        </div>
                    </div>
                    <div className="formRow colorRow">
                        <div className="inputDiv colorInput">
                            <label>Accent Color</label>
                            <div className="colorPickerRow">
                                <input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} className="colorInputField" />
                                <input type="text" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} className="inputField colorText" placeholder="#667eea" />
                            </div>
                        </div>
                        <div className="inputDiv colorInput">
                            <label>Background Color</label>
                            <div className="colorPickerRow">
                                <input type="color" value={form.backgroundColor} onChange={(e) => setForm(f => ({ ...f, backgroundColor: e.target.value }))} className="colorInputField" />
                                <input type="text" value={form.backgroundColor} onChange={(e) => setForm(f => ({ ...f, backgroundColor: e.target.value }))} className="inputField colorText" placeholder="#f8f9ff" />
                            </div>
                        </div>
                    </div>
                    <div className="buttonsRow">
                        <button type="submit" className="primaryBtn" disabled={loading}>{editId ? 'Update' : 'Add'} Category</button>
                        <button type="button" className="secondaryBtn" onClick={resetForm} disabled={loading}>Cancel</button>
                    </div>
                </form>
            )}

            <div className="faqCategoryList">
                {categories.length === 0 && <p className="noDataText">No categories yet. Add one to get started.</p>}
                {categories.map(cat => (
                    <div key={cat._id} className="faqCategoryCard" style={{ borderLeftColor: cat.color || '#667eea' }}>
                        <div className="faqCategoryCardInfo">
                            <span className="faqCategoryColor" style={{ background: cat.color || '#667eea' }}></span>
                            <div>
                                <h4>{cat.name}</h4>
                            </div>
                        </div>
                        <div className="faqCategoryActions">
                            <button type="button" className="iconBtn editBtn" onClick={() => handleEdit(cat)} title="Edit"><FaEdit /></button>
                            <button type="button" className="iconBtn deleteBtn" onClick={() => setDeleteConfirm({ open: true, id: cat._id })} title="Delete"><FaTrash /></button>
                        </div>
                    </div>
                ))}
            </div>

            <ConfirmDialog open={deleteConfirm.open} title="Delete Category" message="This will delete the category and all its FAQ items. Continue?" onConfirm={handleDelete} onCancel={() => setDeleteConfirm({ open: false, id: null })} isLoading={loading} />
        </div>
    );
}

// FAQ Items Tab - ReactQuill with default config (same as AdminBlog for compatibility)
function FAQItemsTab({ categories, faqs, headers, onUpdate }) {
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ question: '', answer: '', category: '', featured: false });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    const resetForm = useCallback(() => {
        setForm({ question: '', answer: '', category: categories[0]?._id || '', featured: false });
        setEditId(null);
        setShowForm(false);
    }, [categories]);

    useEffect(() => {
        if (categories.length && !form.category) setForm(f => ({ ...f, category: categories[0]._id }));
    }, [categories]);

    const handleEdit = (item) => {
        setEditId(item._id);
        setForm({
            question: item.question,
            answer: item.answer,
            category: item.category?._id || item.category,
            featured: !!item.featured
        });
        setShowForm(true);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.question.trim()) return showToast('Question is required', 'warning');
        if (!form.answer.trim()) return showToast('Answer is required', 'warning');
        if (!form.category) return showToast('Please select a category', 'warning');
        setLoading(true);
        try {
            if (editId) {
                await axios.put(`${API}/api/v1/faq/admin/items/${editId}`, form, { headers });
                showToast('FAQ updated', 'success');
            } else {
                await axios.post(`${API}/api/v1/faq/admin/items`, form, { headers });
                showToast('FAQ created', 'success');
            }
            resetForm();
            onUpdate();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error saving FAQ', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm.id) return;
        setLoading(true);
        try {
            await axios.delete(`${API}/api/v1/faq/admin/items/${deleteConfirm.id}`, { headers });
            showToast('FAQ deleted', 'success');
            setDeleteConfirm({ open: false, id: null });
            onUpdate();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error deleting', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="faqItemsTab">
            <div className="faqTabHeader">
                <h3 className="secondaryHeading">FAQ Items</h3>
                <button className="primaryBtn" onClick={() => { resetForm(); setShowForm(true); }} disabled={loading}>
                    <FaPlus /> Add FAQ
                </button>
            </div>

            {showForm && (
                <form className="faqItemForm" onSubmit={handleSubmit}>
                    <div className="formRow">
                        <div className="inputDiv flex2">
                            <label>Question <span>*</span></label>
                            <input type="text" value={form.question} onChange={(e) => setForm(f => ({ ...f, question: e.target.value }))} placeholder="e.g. How do I create an account?" required className="inputField" />
                        </div>
                        <div className="inputDiv">
                            <label>Category</label>
                            <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="inputField dropdownLight" required>
                                <option value="">Select category</option>
                                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="formRow">
                        <div className="inputDiv checkboxRow">
                            <label>
                                <input type="checkbox" checked={form.featured} onChange={(e) => setForm(f => ({ ...f, featured: e.target.checked }))} />
                                Show on Home page
                            </label>
                        </div>
                    </div>
                    <div className="inputDiv faqAnswerEditorWrap">
                        <label>Answer <span>*</span> (supports links, images, formatting)</label>
                        <div className="quillWrapper">
                            <ReactQuill
                                theme="snow"
                                value={form.answer}
                                onChange={(v) => setForm(f => ({ ...f, answer: v }))}
                                className="faqAnswerEditor"
                            />
                        </div>
                        <p className="quillHint">Use the toolbar for bold, lists, links, and images (paste image URL).</p>
                    </div>
                    <div className="buttonsRow">
                        <button type="submit" className="primaryBtn" disabled={loading}>{editId ? 'Update' : 'Add'} FAQ</button>
                        <button type="button" className="secondaryBtn" onClick={resetForm} disabled={loading}>Cancel</button>
                    </div>
                </form>
            )}

            <div className="faqItemList">
                {faqs.length === 0 && <p className="noDataText">No FAQ items yet. Add one to get started.</p>}
                {faqs.map(item => (
                    <div key={item._id} className="faqItemCard">
                        <div className="faqItemCardInfo">
                            <h4>{item.question}</h4>
                            <span className="faqItemMeta">
                                {item.category?.name || 'Uncategorized'}
                                {item.featured && <FaStar className="featuredStar" title="Shown on Home" />}
                            </span>
                        </div>
                        <div className="faqItemActions">
                            <button type="button" className="iconBtn editBtn" onClick={() => handleEdit(item)} title="Edit"><FaEdit /></button>
                            <button type="button" className="iconBtn deleteBtn" onClick={() => setDeleteConfirm({ open: true, id: item._id })} title="Delete"><FaTrash /></button>
                        </div>
                    </div>
                ))}
            </div>

            <ConfirmDialog open={deleteConfirm.open} title="Delete FAQ" message="Are you sure you want to delete this FAQ?" onConfirm={handleDelete} onCancel={() => setDeleteConfirm({ open: false, id: null })} isLoading={loading} />
        </div>
    );
}
